import {
  Badge, Box, Button, Text,
} from '@usealloy/component-library';
import styled, { css, keyframes } from 'styled-components';
import { ContainerType } from '../../NodeEditorDrawer/ThresholdsTab/types';
import type { FeatureFlags } from '~/helpers/typescript';
import { RuleLineNumber } from '../../NodeEditorDrawer/styles';

export const StyledLineBreakDivider = styled.div<{
  dragDropZone: boolean;
  canDrop: boolean;
}>`
  position: relative;
  width: inherit;
  height: 1px;
  transition: all 300ms ease;

  ${({ dragDropZone }) => (dragDropZone
    && css`
      border-radius: var(--cl-radius-1);
  `)}
  ${({ canDrop }) => (canDrop
    && css`
      position: relative;
      height: 19px;
      visibility: visible;
      border-radius: var(--cl-radius-2);
      background: var(--cl-yellow-100);
  `)}
`;

export const StyledIndentation = styled.span<{
  indentLevel: number;
}>`
  display: inline;
  width: ${({ indentLevel }) => (`${indentLevel * 20}px`)};
  min-height: 0px;
`;

export const StyledRuleLineItem = styled.span<{
  dimmed: boolean;
  simpleEditorMode: boolean;
  editable: boolean;
  containerType: ContainerType;
}>`
  position: relative;
  display: flex;
  flex-wrap: ${({ simpleEditorMode }) => (simpleEditorMode ? 'wrap' : 'nowrap')};
  width: fit-content;
  align-items: ${({ containerType }) => (
    containerType === ContainerType.TOOLTIP ? 'flex-start' : 'center'
  )};

  ${({ dimmed }) => (dimmed
    && css`
      opacity: 0.5;
      &:after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: transparent;
      }
    `)}

  ${({ editable }) => (editable
    ? css`
      column-gap: 4px;
    `
    : css`
      column-gap: 2px;
    `)}
`;

const pulseLineNumbers = keyframes`
  0% { color: var(--cl-gray-600); }
  40% { color: var(--cl-gray-1100); }
  100% { color: var(--cl-gray-600); }
`;

export const StyledRuleAndOutputWrapper = styled(Box)<{
  containerType: ContainerType;
  featureFlags: Partial<FeatureFlags>;
  isHighlighted: boolean;
}>`
  &:hover #close-group-hover-button {
    visibility: visible;
  }

  ${({ isHighlighted }) => css`
    padding: 0;
    border-radius: var(--cl-radius-1);

    ${isHighlighted && 'background-color: var(--cl-gray-200);'}

    &:hover {
      background-color: var(--cl-gray-200);

      // Make child rule line numbers light up and pulse on hover of whole row
      > div > ${RuleLineNumber} {
        background-color: var(--cl-gray-500);

        animation: ${pulseLineNumbers};
        animation-duration: 2000ms;
        animation-delay: 0ms;
        animation-iteration-count: infinite;
        animation-timing-function: ease-out;
      }
    }
  `}
`;

export const StyledRuleItemRow = styled.div`
  display: flex;
  flex-direction: row;
`;

export const StyledHighlighter = styled.div<{ isHighlighted: boolean }>`
  ${({ isHighlighted }) => (isHighlighted) && css`
    background-color: var(--cl-yellow-100);
    width: fit-content
  `}
`;

export const StyledRuleLineItemWrapper = styled(Box)<{
  containerType: ContainerType;
  lineWithoutClause: boolean;
  editable: boolean;
}>`
  position: relative;
  overflow: ${({ containerType }) => (containerType === ContainerType.DRAWER ? 'unset' : 'visible')};
  ${({ lineWithoutClause }) => (
    lineWithoutClause
  ) && css`
    margin-left: var(--cl-space-5);
  `}
`;

export const StyledGreenDot = styled.div<{ isVisible: boolean }>`
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  width: var(--cl-space-2);
  height: var(--cl-space-2);
  background-color: var(--cl-green-300);
  border-radius: 100%;
  flex: 0 0 var(--cl-space-2);
  z-index: 1;
`;

/**
 * Max-width pixel value specified to fit within StyledOutputValue
 */
export const StyledOutputBadge = styled(Badge)<{ satisfiesLogic: boolean }>`
  && {
    background-color: ${({ satisfiesLogic }) => (satisfiesLogic ? 'var(--cl-yellow-100)' : 'var(--cl-gray-100)')};
    max-width: 180px;
    overflow: hidden;
    z-index: 1;
  }
`;

export const StyledOutputText = styled(Text)`
  && {
    font-family: var(--cl-font-family-default);
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/**
 * Specific pixel width for output values section
 * See Rule Explainability Figma design
 */
export const StyledOutputValueWrapper = styled.div<{ simpleEditorMode: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-left: 1px solid var(--cl-gray-500);
    width: 194px;
    min-width: 194px;
    min-height: 28px;
    margin-left: var(--cl-space-6);
    padding-left: var(--cl-space-6);
    padding-right: var(--cl-space-3);

  ${({ simpleEditorMode }) => (simpleEditorMode
      && css`
        min-width: auto;
        width: auto;
        padding-left: var(--cl-space-2);
        padding-right: var(--cl-space-2);
      `
  )}
`;

export const StyledEvidencesButton = styled(Button)`
  && {
    height: var(--cl-space-5);
    width: var(--cl-space-5);
    min-width: var(--cl-space-5);
    z-index: 1;
  }
`;

/**
 * Width calculation subtracting width of evidences button and gap
 * This prevents badge/dot from pushing evidences button out of container
 */
export const StyledOutputBadgeAndDot = styled(Box)<{ showEvidenceButton: boolean }>`
  ${({ showEvidenceButton }) => (
    showEvidenceButton && `
      max-width: calc(100% - var(--cl-space-5) - var(--cl-space-1));
    `
  )}
`;
